import { Location } from "@otta/search/schema";

export interface LocationOption {
  label: string;
  value: Location;
  isDisabled?: boolean;
}

export type LabelledLocation = {
  location: Location;
  label: string;
};

// New UK cities that are behind a feature flag and so split out intentionally here
export const NEW_UK_CITIES: LocationOption[] = [
  { label: "Birmingham", value: Location.Birmingham },
  { label: "Bristol", value: Location.Bristol },
  { label: "Cambridge", value: Location.Cambridge },
  { label: "Cardiff", value: Location.Cardiff },
  { label: "Edinburgh", value: Location.Edinburgh },
  { label: "Glasgow", value: Location.Glasgow },
  { label: "Leeds", value: Location.Leeds },
  { label: "Manchester", value: Location.Manchester },
  { label: "Nottingham", value: Location.Nottingham },
  { label: "Oxford", value: Location.Oxford },
];

// Base cities that are always available
export const BASE_CITIES: LocationOption[] = [
  { label: "Amsterdam", value: Location.Amsterdam },
  { label: "Austin", value: Location.AustinUs },
  { label: "Barcelona", value: Location.Barcelona },
  { label: "Berlin", value: Location.Berlin },
  { label: "Boston", value: Location.BostonUs },
  { label: "Chicago", value: Location.ChicagoUs },
  { label: "Denver", value: Location.DenverUs },
  { label: "Dublin", value: Location.Dublin },
  { label: "London", value: Location.London },
  { label: "Los Angeles", value: Location.LosAngelesUs },
  { label: "Miami", value: Location.MiamiUs },
  { label: "New York", value: Location.NewYorkUs },
  { label: "Paris", value: Location.Paris },
  { label: "San Francisco Bay Area", value: Location.SanFranciscoBayAreaUs },
  { label: "Toronto", value: Location.Toronto },
  { label: "Vancouver", value: Location.Vancouver },
];

// Business logic constants - include ALL cities for consistency
export const UK_LOCATIONS = new Set([
  Location.RemoteUk,
  Location.London,
  Location.Birmingham,
  Location.Bristol,
  Location.Cambridge,
  Location.Cardiff,
  Location.Edinburgh,
  Location.Glasgow,
  Location.Leeds,
  Location.Manchester,
  Location.Nottingham,
  Location.Oxford,
]);

export const EU_LOCATIONS = new Set([
  Location.Amsterdam,
  Location.Barcelona,
  Location.Berlin,
  Location.Dublin,
  Location.Paris,
  Location.RemoteFrance,
  Location.RemoteGermany,
  Location.RemoteIreland,
  Location.RemoteNetherlands,
  Location.RemoteSpain,
  Location.RemoteEurope,
]);

export const US_LOCATIONS = new Set([
  Location.AustinUs,
  Location.BostonUs,
  Location.ChicagoUs,
  Location.DenverUs,
  Location.LosAngelesUs,
  Location.MiamiUs,
  Location.NewYorkUs,
  Location.RemoteUs,
  Location.SanFranciscoBayAreaUs,
]);

export const CANADA_LOCATIONS = new Set([
  Location.RemoteCanada,
  Location.Toronto,
  Location.Vancouver,
]);

export const BASE_US_LOCATIONS = [
  Location.AustinUs,
  Location.BostonUs,
  Location.ChicagoUs,
  Location.DenverUs,
  Location.LosAngelesUs,
  Location.MiamiUs,
  Location.NewYorkUs,
  Location.SanFranciscoBayAreaUs,
];

export const BASE_CA_LOCATIONS = [Location.Toronto, Location.Vancouver];

export const BASE_EU_LOCATIONS = [
  Location.Amsterdam,
  Location.Barcelona,
  Location.Berlin,
  Location.Birmingham,
  Location.Bristol,
  Location.Cambridge,
  Location.Cardiff,
  Location.Dublin,
  Location.Edinburgh,
  Location.Glasgow,
  Location.Leeds,
  Location.London,
  Location.Manchester,
  Location.Nottingham,
  Location.Oxford,
  Location.Paris,
];

export enum Country {
  US = "US",
  UK = "UK",
  NL = "NL",
  FR = "FR",
  DE = "DE",
  ES = "ES",
  IE = "IE",
  CA = "CA",
}

export const locationCountries: Partial<Record<Location, Country>> = {
  [Location.Amsterdam]: Country.NL,
  [Location.AustinUs]: Country.US,
  [Location.Barcelona]: Country.ES,
  [Location.Berlin]: Country.DE,
  [Location.Birmingham]: Country.UK,
  [Location.BostonUs]: Country.US,
  [Location.Bristol]: Country.UK,
  [Location.Cambridge]: Country.UK,
  [Location.Cardiff]: Country.UK,
  [Location.ChicagoUs]: Country.US,
  [Location.DenverUs]: Country.US,
  [Location.Dublin]: Country.IE,
  [Location.Edinburgh]: Country.UK,
  [Location.Glasgow]: Country.UK,
  [Location.Leeds]: Country.UK,
  [Location.London]: Country.UK,
  [Location.LosAngelesUs]: Country.US,
  [Location.Manchester]: Country.UK,
  [Location.MiamiUs]: Country.US,
  [Location.NewYorkUs]: Country.US,
  [Location.Nottingham]: Country.UK,
  [Location.Oxford]: Country.UK,
  [Location.Paris]: Country.FR,
  [Location.RemoteCanada]: Country.CA,
  [Location.RemoteFrance]: Country.FR,
  [Location.RemoteGermany]: Country.DE,
  [Location.RemoteIreland]: Country.IE,
  [Location.RemoteNetherlands]: Country.NL,
  [Location.RemoteSpain]: Country.ES,
  [Location.RemoteUk]: Country.UK,
  [Location.RemoteUs]: Country.US,
  [Location.SanFranciscoBayAreaUs]: Country.US,
  [Location.Toronto]: Country.CA,
  [Location.Vancouver]: Country.CA,
};

export type VisaLocation = "US" | "UK" | "EU" | "CA";

export const VISA_LOCATIONS: { value: VisaLocation; label: string }[] = [
  { value: "UK", label: "The UK" },
  { value: "US", label: "The US" },
  { value: "EU", label: "The EU" },
  { value: "CA", label: "Canada" },
];

export type DisplayRegion = "United Kingdom" | "North America" | "Europe";

export function isEUCountry(country?: Country) {
  if (!country) {
    return false;
  }
  return (
    country === Country.FR ||
    country === Country.DE ||
    country === Country.NL ||
    country === Country.ES ||
    country === Country.IE
  );
}

export function isNorthAmericaCountry(country?: Country) {
  if (!country) {
    return false;
  }
  return country === Country.US || country === Country.CA;
}
